import {
	Box,
	Container,
	Divider,
	Dropdown,
	Link,
	Menu,
	MenuButton,
	MenuItem,
	Stack,
	Typography,
} from '@mui/joy';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '~/hooks/auth';

// icons

const Navbar: React.FC = () => {
	const location = useLocation();

	const isLinkActive = (path: string) => {
		const lastSegment = location.pathname.split('/'); // Extracting the last segment after '/'

		if (lastSegment[1] === path) return true;
		if (path === '' && lastSegment.length > 1) {
			return false; // If the path is '/' and there are additional segments in the location.pathname, return false
		}

		if (lastSegment[lastSegment.length - 1] === path) return true;
		for (let i = 0; i < lastSegment.length; i++) {
			if (lastSegment[i] === path) {
				return true; // If any segment matches the path, return true
			}
		}

		return false; // If no match is found, return false
	};

	const navigate = useNavigate();

	const { user } = useAuth();
	return (
		<>
			<Box position="relative" className="main">
				<Stack width="100%" bgcolor="common.black" p={2}>
					<Container maxWidth="md">
						<Stack direction="row" justifyContent="space-between" alignItems="center">
							<Box>
								<img
									src="https://mod-acorn.s3.eu-west-1.amazonaws.com/gok-uk-logo.svg"
									alt="GOK UK"
								/>
							</Box>
							<Typography
								sx={{ display: { xs: 'none', md: 'block' } }}
								textAlign="center"
								level="title-md"
								textColor="common.white"
							>
								MCTC Learner Platform
							</Typography>

							<Link
								sx={{
									textDecoration: 'none',
									'&:hover': {
										textDecoration: 'underline',
									},
									display: { xs: 'none', md: 'block' },
								}}
								href="/logout"
								textAlign="end"
								level="title-md"
								textColor="common.white"
							>
								Sign out
							</Link>
							<Dropdown sx={{ display: { xs: 'block', md: 'none' } }}>
								<MenuButton
									sx={{
										display: {
											xs: 'block',
											md: 'none',
											border: 'none',
											color: 'white',
											'&:hover': {
												backgroundColor: 'transparent',
											},
										},
									}}
								>
									Menu
								</MenuButton>
								<Menu>
									<MenuItem
										sx={(theme) => ({ color: theme.vars.palette.text.primary })}
										onClick={() => {
											navigate('/');
										}}
									>
										Home
									</MenuItem>
									<a
										target="_blank"
										rel="noopener noreferrer"
										href="https://box5260.bluehost.com:2096/"
									>
										<MenuItem
											sx={(theme) => ({
												color: theme.vars.palette.text.primary,
											})}
										>
											Email Inbox
										</MenuItem>
									</a>
									<MenuItem
										sx={(theme) => ({ color: theme.vars.palette.text.primary })}
										onClick={() => {
											navigate('/documents');
										}}
									>
										Documents
									</MenuItem>
									<MenuItem
										sx={(theme) => ({ color: theme.vars.palette.text.primary })}
										onClick={() => {
											navigate('/links');
										}}
									>
										Links
									</MenuItem>
									<MenuItem
										sx={(theme) => ({ color: theme.vars.palette.text.primary })}
										onClick={() => {
											navigate('/news');
										}}
									>
										News
									</MenuItem>
									<MenuItem
										sx={(theme) => ({ color: theme.vars.palette.text.primary })}
										onClick={() => {
											navigate('/forms');
										}}
									>
										Forms
									</MenuItem>
									{user?.permissions?.admin && (
										<>
											<MenuItem
												sx={(theme) => ({
													color: theme.vars.palette.text.primary,
												})}
												onClick={() => {
													navigate('/users');
												}}
											>
												Users
											</MenuItem>
											<MenuItem
												sx={(theme) => ({
													color: theme.vars.palette.text.primary,
												})}
												onClick={() => {
													navigate('/settings');
												}}
											>
												Settings
											</MenuItem>
										</>
									)}
									<MenuItem
										sx={(theme) => ({ color: theme.vars.palette.text.primary })}
										onClick={() => {
											navigate('/account');
										}}
									>
										Account
									</MenuItem>
									<MenuItem
										sx={(theme) => ({ color: theme.vars.palette.text.primary })}
										onClick={() => {
											navigate('/logout');
										}}
									>
										Logout
									</MenuItem>
								</Menu>
							</Dropdown>
						</Stack>
					</Container>
				</Stack>
				<Container maxWidth="md">
					<Box
						width="100%"
						height={10}
						sx={(theme) => ({ backgroundColor: theme.vars.palette.primary[500] })}
					></Box>
				</Container>

				<Container maxWidth="md" sx={{ display: { md: 'block', xs: 'none' } }}>
					<Stack direction="row" alignItems="center" spacing={4} my={2}>
						<Link
							fontWeight={isLinkActive('') ? 'bold' : 'normal'}
							href="/"
							level="body-md"
							sx={{ textDecoration: 'none' }}
							textColor="text.primary"
						>
							Home
						</Link>
						<Link
							fontWeight={isLinkActive('documents') ? 'bold' : 'normal'}
							textColor="text.primary"
							href="/documents"
							level="body-md"
							sx={{ textDecoration: 'none' }}
						>
							Documents
						</Link>

						<Link
							fontWeight={isLinkActive('links') ? 'bold' : 'normal'}
							textColor="text.primary"
							href="/links"
							level="body-md"
							sx={{ textDecoration: 'none' }}
						>
							Links
						</Link>
						<Link
							fontWeight={isLinkActive('news') ? 'bold' : 'normal'}
							textColor="text.primary"
							href="/news"
							level="body-md"
							sx={{ textDecoration: 'none' }}
						>
							News
						</Link>
						<Link
							flex={!user?.permissions?.admin ? 1 : undefined}
							fontWeight={isLinkActive('forms') ? 'bold' : 'normal'}
							textColor="text.primary"
							href="/forms"
							level="body-md"
							sx={{ textDecoration: 'none' }}
						>
							Forms
						</Link>
						{user?.permissions?.admin && (
							<>
								<Link
									fontWeight={isLinkActive('users') ? 'bold' : 'normal'}
									textColor="text.primary"
									href="/users"
									level="body-md"
									sx={{ textDecoration: 'none' }}
								>
									Users
								</Link>
								<Link
									fontWeight={isLinkActive('settings') ? 'bold' : 'normal'}
									textColor="text.primary"
									sx={{ textDecoration: 'none' }}
									href="/settings"
									level="body-md"
								>
									Settings
								</Link>

								<Link
									component={'a'}
									sx={{ flex: 1, textDecoration: 'none' }}
									target="_blank"
									rel="noopener noreferrer"
									href="https://box5260.bluehost.com:2096/"
									fontWeight={isLinkActive('settings') ? 'bold' : 'normal'}
									textColor="text.primary"
									level="body-md"
								>
									Email Inbox
								</Link>
							</>
						)}

						<Link
							fontWeight={isLinkActive('account') ? 'bold' : 'normal'}
							textColor="text.primary"
							href="/account"
							level="body-md"
							sx={{ textDecoration: 'none' }}
						>
							Account
						</Link>
					</Stack>
					<Divider />
				</Container>
			</Box>
		</>
	);
};

export default Navbar;
